import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Vote() {
  const [items, setItems] = useState([]);
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/vote/feed").then(r => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const commission = async (id) => {
    if (!user) { nav("/login"); return; }
    if ((user.credits || 0) < 200) { toast.error("Need 200 credits"); nav("/pricing"); return; }
    if (!window.confirm("Commission this gallery instantly for 200 credits?")) return;
    setBusy(id);
    try {
      await api.post(`/commission/${id}`);
      await refresh(); await load();
      toast.success("Commissioned! 🎬 Gallery is greenlit.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const vote = async (id) => {
    if (!user) { nav("/login"); return; }
    if ((user.credits || 0) < 20) { toast.error("Need 20 credits"); nav("/pricing"); return; }
    setBusy(id);
    try {
      const { data } = await api.post(`/vote/${id}`);
      await refresh(); await load();
      toast.success(data.greenlit ? "GREENLIT! 🎬" : `Vote cast — ${data.votes}/10`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24">
        <div className="overline mb-3">Community · Greenlight</div>
        <h1 className="font-serif-editorial text-5xl md:text-7xl tracking-tighter mb-4 max-w-4xl">
          Ten votes to bring it to life.
        </h1>
        <p className="text-neutral-500 max-w-xl mb-14">
          A teaser reaching 10 votes is greenlit for a full editorial. Each vote costs 20 credits.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger">
          {items.map((it) => {
            const pct = Math.min(100, (it.votes / 10) * 100);
            return (
              <div key={it.image_id} className="border border-white/10" data-testid={`vote-card-${it.image_id}`}>
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={it.url} alt="" className="w-full h-full object-cover thumb-locked"/>
                </div>
                <div className="p-6">
                  <div className="overline mb-2">{it.model_name}</div>
                  <div className="font-serif-editorial text-2xl tracking-tight mb-4">{it.gallery_title}</div>
                  <div className="progress-track mb-2"><div className="progress-fill" style={{width: `${pct}%`}}/></div>
                  <div className="flex items-center justify-between text-xs font-mono-ui mb-6">
                    <span className="text-neutral-500">{it.votes}/10 votes</span>
                    <span className={it.status === "greenlit" || it.status === "released" ? "text-white" : "text-neutral-500"}>
                      {it.status === "released" ? "RELEASED" :
                       it.status === "greenlit" ? (it.via === "instant_commission" ? "COMMISSIONED" : "GREENLIT") :
                       "PENDING"}
                    </span>
                  </div>
                  {it.status === "released" ? (
                    <button onClick={() => nav(`/models/${it.model_id}`)} className="btn-ghost w-full justify-center" data-testid={`view-released-${it.image_id}`}>
                      View released gallery →
                    </button>
                  ) : it.status === "greenlit" ? (
                    <div className="btn-ghost w-full justify-center opacity-60 cursor-default">In production</div>
                  ) : it.voted ? (
                    <div className="text-center text-xs font-mono-ui text-neutral-400 py-4 border border-white/10" data-testid={`already-voted-${it.image_id}`}>
                      You've already voted
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        disabled={busy === it.image_id}
                        onClick={() => vote(it.image_id)}
                        className="btn-white w-full justify-center"
                        data-testid={`vote-btn-${it.image_id}`}>
                        {busy === it.image_id ? "..." : "Vote · 20¢"}
                      </button>
                      <button
                        disabled={busy === it.image_id}
                        onClick={() => commission(it.image_id)}
                        className="btn-ghost w-full justify-center !text-[10px]"
                        data-testid={`commission-btn-${it.image_id}`}>
                        Skip the wait — commission instantly · 200¢
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
